import { useMemo } from 'react'

import { useIntl } from 'react-intl'

import CategoricalFilter from '../../shared/CategoricalFilter'
import { createCategoricalAppliedComponent } from '../../shared/filterUtils'
import toDisplayNames from '../../toDisplayNames'
import type { ListingFilter, FilterProps } from '../../types'

const key = 'warehouseLocation'
const toDisplayName = toDisplayNames[key]

const WarehouseLocationFilter: ListingFilter<Set<string>> = ({
  onChange,
  desktopScreen,
  items: itemsData,
  value,
}: FilterProps<Set<string>>) => {
  const intl = useIntl()

  // Sort items alphabetically by state, then by city within each state.
  // Values are in the format COUNTRY/STATE/City (e.g. US/CA/Los Angeles).
  const items = useMemo(() => {
    if (!itemsData) return []

    return itemsData
      .filter(
        (option): option is { value: string; count?: number } =>
          typeof option.value === 'string'
      )
      .map((option) => ({
        key: option.value,
        displayName: toDisplayName(option.value),
      }))
      .sort((a, b) => {
        const aParts = a.key.split('/')
        const bParts = b.key.split('/')
        const aCountry = aParts[0] ?? ''
        const bCountry = bParts[0] ?? ''
        const aState = aParts[1] ?? ''
        const bState = bParts[1] ?? ''
        const aCity = aParts.at(-1) ?? ''
        const bCity = bParts.at(-1) ?? ''

        // US locations always come before international
        if (aCountry === 'US' && bCountry !== 'US') return -1
        if (aCountry !== 'US' && bCountry === 'US') return 1

        // International: group by country first, then state/region, then city
        const countryCompare = aCountry.localeCompare(bCountry)
        if (countryCompare !== 0) return countryCompare

        const stateCompare = aState.localeCompare(bState)
        if (stateCompare !== 0) return stateCompare
        return aCity.localeCompare(bCity)
      })
  }, [itemsData])

  return (
    <CategoricalFilter
      items={items}
      selected={value}
      name={intl.formatMessage({
        id: `Filters.${key}`,
      })}
      onChange={onChange}
      desktopScreen={desktopScreen}
      sortOrder="column"
    />
  )
}

WarehouseLocationFilter.expandedOnInit = true
WarehouseLocationFilter.getOwnProps = ({ state, data, updateFilters }) => {
  return {
    filterKey: key,
    items: data?.allFilters[key] || [],
    value: new Set(state.filters[key]),
    onChange: (value: Set<string>) => updateFilters({ [key]: [...value] }),
  }
}

WarehouseLocationFilter.label = `Filters.${key}`

WarehouseLocationFilter.Applied = createCategoricalAppliedComponent(
  key,
  WarehouseLocationFilter.label,
  toDisplayName
)

export default WarehouseLocationFilter
